import React, { useEffect, useContext, useState, useRef } from "react"
import AvailabilityHours from "../../../../components/scheduling/availability/AvailabilityHours"
import CalendarDetails from "../../../../components/account-settings/calendars/CalendarDetails"
import Loader from "../../../../components/ui/loader"
import OutOfOffice from "../../../../components/scheduling/availability/OutOfOffice"
import useScrollToTop from "../../../../hooks/useScrollToTop"
import { Button } from "../../../../components/ui/button"
import { CalendarRequests } from "../../../../api/app.service"
import { CommonFunctionCtx } from "../../../../context/commonFunctionContext"
import { ConnectCalendarCTA } from "../../../../components/account-settings/calendars/ConnectCalendarCTA"
import { defaultAvailability } from "../../../../objects/availabilityObjects"
import { ListCalendars } from "../../../../components/account-settings/calendars/ListCalendars"
import { SvgFilledInformationCircle } from "../../../../components/icons"
import { UserCtx } from "../../../../context/userContext"
import { useToast } from "../../../../hooks/useToast"
import NewFeature from "../../../../components/NewFeature"

const CalendarSettingsPage: React.FC = () => {
  const [showCalendarTab, setShowCalendarTab] = useState(true)
  const [showSocialTab, setShowSocialTab] = useState(false)
  const [userCalendars, setUserCalendars] = useState<any>({})
  const [userCalendarsLoading, setUserCalendarsLoading] = useState(false)
  const [availabilities, setAvailabilities] = useState<any[]>([])
  const [shouldRefetchAvailability, setShouldRefetchAvailability] =
    useState(false)

  const { user } = useContext(UserCtx)
  const { updateNextStepsData } = useContext(CommonFunctionCtx)
  const { toast } = useToast()

  const [showCalendarDetails, setShowCalendarDetails] = useState(false)
  const registerRan = useRef(false)

  const getAvailabilityRules = () => {
    if (!user) return
    CalendarRequests.getAvailabilityRules({ user_id: parseInt(user.user_id) })
      .then((data) => setAvailabilities(data.availability_rules))
      .catch((ex) => {
        toast.error(ex.response?.data?.message)
      })
  }

  const userDefaultAvailability = (name: string, is_default: boolean) => ({
    ...defaultAvailability,
    is_default,
    name,
    timezone: user!.timezone,
  })

  const setDefaultAvailability = async () => {
    console.log(user, userCalendars, availabilities)
    if (!user || userCalendars.profile_count > 1) return
    try {
      const rules =
        availabilities.length === 0
          ? [userDefaultAvailability("Default Availability", true)]
          : availabilities
      await CalendarRequests.setAvailabilityRules({
        user_id: parseInt(user.user_id),
        availability_rules: rules,
      })

      getAvailabilityRules()
    } catch (ex: any) {
      toast.error(ex.response?.data?.message || "Error setting availability")
    }
  }

  const scrollToBottom = () => window.scrollTo(0, document.body.scrollHeight)

  const completeRegisterCalendar = async (code: string, data: any) => {
    if (!code || !user || registerRan.current) return
    registerRan.current = true

    window.history.replaceState(null, "", window.location.pathname)

    try {
      await CalendarRequests.registerCalendar({
        user_id: parseInt(user.user_id),
        profile_count: data.profile_count,
        calendar_connect_code: code,
        redirect_url: `${process.env.REACT_APP_BASE_URL}/account-settings/calendars`,
      })
      toast.success("Calendar connected!")
      let data_temp = await fetchUserCalendars()
      scrollToBottom()

      if (user?.isCoach && data_temp.profile_count <= 1) {
        await setDefaultAvailability()
      }

      setShouldRefetchAvailability((prev) => !prev)
      updateNextStepsData(false)
    } catch (ex: any) {
      toast.error(ex.response?.data?.error_detail)
      setUserCalendarsLoading(false)
    }
  }

  const checkUrlParams = (data: any) => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    if (code && user) {
      completeRegisterCalendar(code, data)
    } else {
      window.history.replaceState(null, "", window.location.pathname)
      setUserCalendarsLoading(false)
    }
  }

  const fetchUserCalendars = async () => {
    setUserCalendarsLoading(true)
    if (!user) {
      setUserCalendarsLoading(false)
      return
    }

    try {
      const data: any = await CalendarRequests.getCalendarList({
        user_id: parseInt(user.user_id),
      })

      setUserCalendars({
        profileCount: data.profile_count,
        profiles: data.profiles,
      })

      // If there's only 1 profile, set its first calendar as primary
      if (data.profile_count === 1 && data.profiles[0].calendars?.length) {
        const firstCalendarId = data.profiles[0].calendars[0].calendar_id
        try {
          await CalendarRequests.setPrimaryCalendar({
            user_id: parseInt(user.user_id),
            calendar_id: firstCalendarId,
          })
          const updatedProfiles = data.profiles.map((profile: any) => ({
            ...profile,
            calendars: profile.calendars.map((cal: any) => ({
              ...cal,
              is_primary: cal.calendar_id === firstCalendarId,
            })),
          }))

          setUserCalendars({
            profileCount: data.profile_count,
            profiles: updatedProfiles,
          })
        } catch (err: any) {
          console.error("Failed to set primary calendar:", err)
          toast.error(
            err?.response?.data?.message || "Failed to set primary calendar"
          )
        }
      }

      checkUrlParams(data)
      return data
    } catch (ex: any) {
      toast.error(ex.response?.data?.message || "Error fetching calendars")
    } finally {
      setUserCalendarsLoading(false)
    }
  }


  useEffect(() => {
    getAvailabilityRules()
    fetchUserCalendars()
  }, [user])

  useScrollToTop(showCalendarTab, showSocialTab)

  return (
    <>
      {user?.isCoach && userCalendars.profileCount > 0 && <NewFeature />}
      <div className="mx-auto flex w-full max-w-screen-xl flex-col px-4 sm:px-6 md:px-8 xl:px-28 pt-6 md:pt-0">
        <div className="flex flex-col items-center justify-center md:items-start md:justify-start w-full max-w-screen-xl mx-auto pt-6 md:pt-0 mb-8">
          <div className="flex items-center justify-center w-full md:justify-start gap-2">
            <h1 className="text-[36px] font-bold text-black text-center md:text-left">
              Calendar Settings
            </h1>
            <Button
              onClick={() => setShowCalendarDetails(true)}
              variant="link"
              size="icon"
              className="flex"
            >
              <SvgFilledInformationCircle className="w-4 h-4" />
            </Button>
          </div>
          <p className="mt-2 mb-4 text-[#3A434A] text-base text-center md:text-left w-full md:w-auto">
            Watch our{" "}
            <a
              href="https://intercom.help/zoee/en/articles/8685614-calendar-integration-for-scheduling-and-video-sessions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              <span className="font-bold text-blurple">quick video guide</span>
            </a>{" "}
            to get started in minutes and{" "}
            {user.activeProfile === "coach"
              ? "make scheduling effortless on Zoee."
              : "enjoy effortless booking on Zoee."}
          </p>
        </div>

        {!userCalendarsLoading ? (
          <>
            {showCalendarTab && (
              <div className="flex flex-col gap-6">
                <ListCalendars
                  userCalendars={userCalendars}
                  fetchUserCalendars={fetchUserCalendars}
                />
                {userCalendars.profileCount === 0 && (
                  <ConnectCalendarCTA
                    isCalendarConnected={userCalendars.profileCount > 0}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex w-full flex-col items-center gap-[12px]">
            <Loader />
            <h3 className="mb-[4px] text-center text-[16px] font-bold">
              Checking for calendars...
            </h3>
          </div>
        )}

        {userCalendars.profileCount > 0 && user?.isCoach && (
          <>
            <div className="mt-6">
              <OutOfOffice calendar={userCalendars} />
            </div>
            <div className="mt-6">
              <AvailabilityHours refetchTrigger={shouldRefetchAvailability} />
            </div>
            <div className="mb-6"></div>
          </>
        )}
      </div>

      {showCalendarDetails && (
        <CalendarDetails setShowCalendarDetails={setShowCalendarDetails} />
      )}
    </>
  )
}

export default CalendarSettingsPage
